import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../utils/cdn';
import { getApiUrl } from '../utils/apiUrl';
import './ProductDetail.css';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiUrl()}/products/${slug}`
      );
      setProduct(response.data?.product || null);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product || product.stock_status === 'OUT OF STOCK') return;

    setAddingToCart(true);
    const result = await addToCart(product.id, quantity);
    setAddingToCart(false);

    if (result.success) {
      alert('Product added to cart!');
    } else {
      alert(result.error || 'Failed to add to cart');
    }
  };

  if (loading) {
    return <div className="loading">Loading product...</div>;
  }

  if (!product) {
    return <div className="error">Product not found</div>;
  }

  const isSoldOut = product.stock_status === 'OUT OF STOCK' || !product.stock_status;
  const galleryImages = product.gallery_images && Array.isArray(product.gallery_images) 
    ? product.gallery_images 
    : [];

  return (
    <>
      <Helmet>
        <title>{product.meta_title || product.name} - NieHairLabel</title>
        <meta name="description" content={product.meta_description || product.short_description || product.description} />
        {product.seo_keywords && <meta name="keywords" content={product.seo_keywords} />}
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.short_description || product.description} />
        {product.image_url && (
          <meta property="og:image" content={getImageUrl(product.image_url)} />
        )}
      </Helmet>

      <div className="product-detail">
        <div className="container">
          <div className="product-detail-content">
            <div className="product-images">
              <div className="main-image">
                <img
                  src={getImageUrl(product.image_url || '/placeholder.jpg')}
                  alt={product.name}
                />
              </div>
              {galleryImages.length > 0 && (
                <div className="gallery-images">
                  {galleryImages.map((img, index) => (
                    <img
                      key={index}
                      src={getImageUrl(img)}
                      alt={`${product.name} ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="product-info">
              <h1>{product.name}</h1>
              
              <div className="product-price">
                <span className="price">${product.price}</span>
                {product.compare_price && (
                  <span className="compare-price">${product.compare_price}</span>
                )}
              </div>

              {product.short_description && (
                <p className="short-description">{product.short_description}</p>
              )}

              {product.description && (
                <div className="description">
                  <h3>Description</h3>
                  <div dangerouslySetInnerHTML={{ __html: product.description.replace(/\n/g, '<br />') }} />
                </div>
              )}

              <div className="stock-info">
                {product.stock_status === 'OUT OF STOCK' || isSoldOut ? (
                  <span className="sold-out">Out of Stock</span>
                ) : (
                  <span className="in-stock">In Stock</span>
                )}
              </div>

              {!isSoldOut && (
                <div className="add-to-cart-section">
                  <div className="quantity-selector">
                    <label>Quantity:</label>
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                  </div>

                  <button
                    className="btn btn-primary btn-large"
                    onClick={handleAddToCart}
                    disabled={addingToCart || isSoldOut}
                  >
                    {addingToCart ? 'Adding...' : isSoldOut ? 'Out of Stock' : 'Add to Cart'}
                  </button>
                </div>
              )}

              {product.sku && (
                <div className="product-meta">
                  <p><strong>SKU:</strong> {product.sku}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetail;

